import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Product } from '@/types';
import { useCart } from '@/contexts/CartContext';
import { Plus, Minus, Check } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

type ProductCardProps = {
  product: Product;
};

export function ProductCard({ product }: ProductCardProps) {
  const { hotelId } = useParams<{ hotelId: string }>();
  const { addToCart, items, updateQuantity, removeFromCart } = useCart();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [showControls, setShowControls] = useState(false);
  
  // Check if product is in cart
  const cartItem = items.find(item => item.product.id === product.id);
  const quantity = cartItem ? cartItem.quantity : 0;
  
  // Update controls visibility based on cart
  useEffect(() => {
    if (quantity > 0) {
      setShowControls(true);
    }
  }, [quantity]);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (quantity === 0) {
      addToCart(product, 1);
      setShowControls(true);
      toast({
        title: "Added to cart",
        description: product.name,
        duration: 2000,
      });
    }
  };
  
  const handleIncreaseQuantity = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    updateQuantity(product.id, quantity + 1);
  };
  
  const handleDecreaseQuantity = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (quantity > 0) {
      if (quantity === 1) {
        removeFromCart(product.id);
        setShowControls(false);
      } else {
        updateQuantity(product.id, quantity - 1);
      }
    }
  };

  return (
    <Link to={`/${hotelId}/product/${product.id}`}>
      <div className="flex border-b py-3">
        <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0 relative">
          <img
            src={product.image}
            alt={product.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
          
          {quantity > 0 && (
            <div className="absolute top-0 left-0 bg-primary text-primary-foreground text-xs font-medium h-5 w-5 flex items-center justify-center rounded-br-md">
              {quantity}
            </div>
          )}
        </div>
        
        <div className="ml-3 flex-1 flex flex-col justify-between">
          <div>
            <h3 className="font-medium text-sm line-clamp-1">{product.name}</h3>
            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
              {product.description}
            </p>
          </div>
          
          <div className="flex items-center justify-between">
            <p className="font-bold text-primary">
            {t('currency')}{product.price.toFixed(2)}
            </p>
            
            {showControls ? (
              <div className="flex items-center border rounded">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 rounded-r-none"
                  onClick={handleDecreaseQuantity}
                >
                  <Minus className="h-3 w-3" />
                </Button>
                
                <div className="flex h-8 w-8 items-center justify-center text-xs border-x">
                  {quantity}
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 rounded-l-none"
                  onClick={handleIncreaseQuantity}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <Button
                variant="default"
                size="sm"
                className="h-8 w-8 rounded-full p-0"
                onClick={handleAddToCart}
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}